// The tile overflow menu.
//
// Tile controls used to be a row of icon buttons pinned to the thumbnail, and
// the row kept growing — prompt, download, share, remove, report — until the
// icons were both unreadable and, on a phone-sized tile, larger than the
// artwork they sat on. One "⋯" opens a labelled list instead: the actions get
// names rather than glyphs a visitor has to decode, and adding another one
// costs no thumbnail space.
//
// Only one menu is open at a time, and it is appended to <body> rather than
// the tile so a scrolling or clipped tile cannot cut it off.

const OPEN_CLASS = 'has-menu-open';
let open = null; // { menu, anchor, onClose }

function closeOpen() {
  if (!open) return;
  const { menu, anchor } = open;
  menu.remove();
  anchor.setAttribute('aria-expanded', 'false');
  anchor.closest('.fav-tile, .slot, .sample-tile')?.classList.remove(OPEN_CLASS);
  open = null;
}

export function closeActionMenu() {
  closeOpen();
}

// Global dismissal. Registered once, at module load: a listener per menu would
// leak one per tile render, and the wall re-renders on every heart.
document.addEventListener('click', (e) => {
  if (!open) return;
  if (open.menu.contains(e.target) || open.anchor.contains(e.target)) return;
  closeOpen();
});
document.addEventListener('keydown', (e) => {
  if (!open) return;
  if (e.key === 'Escape') {
    e.stopPropagation();
    const { anchor } = open;
    closeOpen();
    anchor.focus();
  }
});
// A menu positioned against the viewport has to go when the viewport moves.
// Capture phase so a scroll inside the tile grid counts, not just the page.
window.addEventListener('scroll', closeOpen, true);
window.addEventListener('resize', closeOpen);

function place(menu, anchor) {
  const rect = anchor.getBoundingClientRect();
  const { width, height } = menu.getBoundingClientRect();
  const margin = 8;
  // Prefer below-right of the button, then flip on whichever axis runs out.
  let left = rect.right - width;
  let top = rect.bottom + 4;
  if (left < margin) left = margin;
  if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
  if (top + height > window.innerHeight - margin) top = rect.top - height - 4;
  if (top < margin) top = margin;
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
}

/**
 * Attach an overflow menu button to a tile's action cluster.
 *
 * @param {HTMLElement} box    the .tile-actions cluster
 * @param {() => Array} build  called on each open, so an item's label or
 *                             availability can reflect state that changed
 *                             since the tile was drawn. Return items shaped
 *                             { label, icon, danger?, disabled?, note?, onClick }.
 *                             Falsy entries are skipped, so callers can inline
 *                             conditionals.
 * @param {string} [label]     accessible name for the button
 */
export function addActionMenu(box, build, label = 'More actions') {
  const anchor = document.createElement('button');
  anchor.type = 'button';
  anchor.className = 'tile-more';
  anchor.title = label;
  anchor.setAttribute('aria-label', label);
  anchor.setAttribute('aria-haspopup', 'menu');
  anchor.setAttribute('aria-expanded', 'false');
  anchor.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><use href="#i-more"/></svg>';

  anchor.onclick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const wasMine = open?.anchor === anchor;
    closeOpen();
    if (wasMine) return; // second click on the same button closes it

    const items = (build() || []).filter(Boolean);
    if (!items.length) return;

    const menu = document.createElement('div');
    menu.className = 'tile-menu';
    menu.setAttribute('role', 'menu');
    for (const item of items) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'menuitem');
      btn.className = item.danger ? 'danger' : '';
      btn.disabled = !!item.disabled;
      const glyph = item.icon
        ? `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#i-${item.icon}"/></svg>`
        : '';
      // Labels and notes are set as text nodes below; only our own glyph
      // markup goes in as HTML.
      btn.innerHTML = `${glyph}<span class="tile-menu-label"></span>`;
      btn.querySelector('.tile-menu-label').textContent = item.label;
      if (item.note) {
        const note = document.createElement('span');
        note.className = 'tile-menu-note';
        note.textContent = item.note;
        btn.appendChild(note);
      }
      btn.onclick = (ev) => {
        ev.stopPropagation();
        closeOpen();
        item.onClick?.(ev);
      };
      menu.appendChild(btn);
    }

    document.body.appendChild(menu);
    place(menu, anchor);
    anchor.setAttribute('aria-expanded', 'true');
    // The tile keeps its hover treatment while its menu is open, so the
    // controls do not fade out from under the pointer that opened them.
    anchor.closest('.fav-tile, .slot, .sample-tile')?.classList.add(OPEN_CLASS);
    open = { menu, anchor };
    menu.querySelector('button:not([disabled])')?.focus();
  };

  box.appendChild(anchor);
  return anchor;
}
